import {NgIf} from '@angular/common';
import {Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {ActivatedRoute, Router, RouterOutlet} from '@angular/router';
import {FuseFullscreenComponent} from '@fuse/components/fullscreen';
import {FuseLoadingBarComponent} from '@fuse/components/loading-bar';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import {FuseMediaWatcherService} from '@fuse/services/media-watcher';
import {NavigationService} from 'app/core/navigation/navigation.service';
import {Navigation} from 'app/core/navigation/navigation.types';
import {LanguagesComponent} from 'app/layout/common/languages/languages.component';
import {MessagesComponent} from 'app/layout/common/messages/messages.component';
import {NotificationsComponent} from 'app/layout/common/notifications/notifications.component';
import {QuickChatComponent} from 'app/layout/common/quick-chat/quick-chat.component';
import {SearchComponent} from 'app/layout/common/search/search.component';
import {ShortcutsComponent} from 'app/layout/common/shortcuts/shortcuts.component';
import {UserComponent} from 'app/layout/common/user/user.component';
import {Subject, takeUntil} from 'rxjs';
import {AuthService} from '../../../../core/auth/auth.service';
import {SignInService} from 'app/modules/auth/sign-in/sign-in.service';
import {SharedModule} from 'app/modules/shared/shared.module';
import {parseDomain, ParseResultType} from "parse-domain";

@Component({
    selector: 'classic-layout',
    templateUrl: './classic.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        MatButtonModule,
        MatIconModule,
        LanguagesComponent,
        FuseFullscreenComponent,
        SearchComponent,
        ShortcutsComponent,
        MessagesComponent,
        NotificationsComponent,
        UserComponent,
        NgIf,
        RouterOutlet,
        QuickChatComponent,
        SharedModule,
    ],
})
export class ClassicLayoutComponent implements OnInit, OnDestroy {
    memberInfo: any;
    isScreenSmall: boolean;
    navigation: Navigation;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    company_name: string;
    company_logo: string;

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _authService: AuthService,
        private signInService: SignInService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        /* Get the current hostname */
        // const hostname = "etrace-eagro.com";
        const hostname = window.location.hostname;

        console.log("Hostname: ", hostname);

        /* Function to parse the root domain */
        const getMainDomain = (hostname: string): string | null => {
            const parseResult = parseDomain(hostname);

            console.log("Parse result: ", parseResult);

            if (parseResult.type === ParseResultType.Listed) {
                const {domain, topLevelDomains} = parseResult;
                return `${domain}.${topLevelDomains.join('.')}`;
            } else if (parseResult.type === ParseResultType.Reserved) {
                return `${parseResult.hostname}`
            }

            return null;
        };

        /* Get the main domain */
        const rootDomain = getMainDomain(hostname);

        this.signInService
            .getMemberInfo(rootDomain)
            .then((data) => {
                if (data && data.company_logo) {
                    this.company_logo = data.company_logo;
                }
                if (data && data.company_name) {
                    this.company_name = data.company_name;
                }
            })
            .catch((error) => {
                console.error('Failed to get auth company info.', error);
            });

        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({matchingAliases}) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    /**
     * Sign out
     */
    signOut(): void {
        this._authService.removeAccessToken();
        this._router.navigate(['/sign-out']).then();
    }

    /**
     * Navigate to home
     */
    navigateToHome(): void {
        this._router.navigate(['/portal/points/redemption-history']);
    }
}
